// Stuck-applications tile — Session 7 PR2.
//
// Rendered between the recommendation tile and the shortlist on Home.
// Empty (no stuck rows) → tile not rendered at all (the parent decides);
// non-empty → compact list with age + optional "looks similar to {co}"
// annotation pulled from the centroid similarity computation server-side.

import { useNavigate } from "react-router";
import { ExternalLink, AlertTriangle } from "lucide-react";
import { Card, CardBody, CardHeader } from "../ui/Card";
import { Button } from "../ui/Button";
import { Pill } from "../ui/Pill";
import { Skeleton } from "../ui/Skeleton";
import {
  useStuckApplications,
  type StuckApplication,
} from "../../lib/stuck";

export function StuckApplicationsTile() {
  const stuck = useStuckApplications();

  if (stuck.isLoading) {
    return (
      <Card>
        <CardHeader title="Needs follow-up" />
        <CardBody>
          <ul className="space-y-2">
            {[0, 1].map((i) => (
              <li key={i} className="flex items-center justify-between gap-4 py-2">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="h-6 w-16" />
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>
    );
  }

  // Hide the tile when nothing's stuck. Empty state would just be noise on
  // a Home dashboard otherwise — the headline on /insights already shows
  // the count for users who want it.
  if (stuck.error || !stuck.data || stuck.data.count === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Needs follow-up"
        description="Applications that have been in `applied` for over 14 days with no interview yet — worth a check-in or a withdrawn flag."
      />
      <CardBody>
        <ul className="divide-y divide-neutral-800">
          {stuck.data.stuck.map((row) => (
            <StuckRow key={row.application_id} row={row} />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

function StuckRow({ row }: { row: StuckApplication }) {
  const navigate = useNavigate();
  const similar = row.similar_to_progressed;

  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          {row.company_name}
        </p>
        <p className="mt-0.5 truncate text-sm font-medium text-neutral-100">
          {row.role}
        </p>
        {similar ? (
          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-neutral-400">
            <AlertTriangle size={11} aria-hidden className="shrink-0 text-amber-400/80" />
            <span className="truncate">
              Looks similar to {similar.role} where you reached interviewing
            </span>
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 sm:shrink-0">
        <Pill tone="warning">{row.age_days}d in applied</Pill>
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-700 bg-neutral-800/40 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600 hover:bg-neutral-800"
        >
          Open posting
          <ExternalLink size={12} aria-hidden />
        </a>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/applications`)}
        >
          Open application
        </Button>
      </div>
    </li>
  );
}
